import React from 'react';
import styles from './StenosisReportModalContent.module.scss';
import { REPORT_MODAL_NAMES } from 'config/labels';

export interface StenosisRow {
  vessel: string;
  percent?: number | null;
  plaque?: string;
}
export interface StenosisReportData {
  calciumScore: number;
  imageUrl: string;
  rows: StenosisRow[];
}
export interface SegmentRow {
  vessel: string;
  proximal: string;
  mid: string;
  distal: string;
}
export interface SideBranchData {
  name: string;
  value: string;
}
interface Props {
  data: StenosisReportData;
  segmentRows?: SegmentRow[];
  branchRows?: SideBranchData[];
  onClose: () => void;
}

export default function StenosisReportModalContent({
  data,
  segmentRows,
  branchRows,
}: Props) {
  const { imageLabel } = REPORT_MODAL_NAMES.stenosis;

  const resolvedSegmentRows = segmentRows ?? [
    { vessel: 'LM', proximal: '', mid: '0', distal: '' },
    { vessel: 'LAD', proximal: '0', mid: '0', distal: '0' },
    { vessel: 'LCX', proximal: '0', mid: '-', distal: '0' },
    { vessel: 'RCA', proximal: '0', mid: '0', distal: '0' },
  ];

  const sideData = branchRows ?? [
    { name: 'D1', value: '0' },
    { name: 'D2', value: '0' },
    { name: 'RI', value: '0' },
    { name: 'OM1', value: '0' },
    { name: 'OM2', value: '0' },
    { name: 'PDA', value: '0' },
    { name: 'PLB', value: '0' },
  ];

  return (
    <div className={styles.report}>
      <div className={styles.reportContent}>
        <div className={styles.reportRow}>
          <div className={styles.leftPane}>
            <div className={styles.leftPaneInner}>
              <div className={styles.header}>
                <div className={styles.headerLine}></div>
                <span className={styles.headerTitleSmall}>{imageLabel}</span>
              </div>
              {data.imageUrl ? (
                <img
                  className={styles.modelImage}
                  src={data.imageUrl}
                  alt="3D model"
                />
              ) : (
                <div className={styles.modelImage} />
              )}
            </div>
          </div>

          <div className={styles.rightPane}>
            <div className={styles.rightTable}>
              <div className={styles.rightTableInner}>
                <div className={styles.rightContent}>
                  <div className={styles.rightTop}>
                    <div className={styles.rightHeader}>
                      <div className={styles.header}>
                        <span className={styles.headerTitleWide}>
                          Interpretation
                        </span>
                      </div>
                    </div>

                    <div className={styles.mainSection}>
                      <div className={styles.mainSectionInner}>
                        <div className={styles.mainSectionBlock}>
                          <div className={styles.header}>
                            <div className={styles.headerLine}></div>
                            <span className={styles.headerTitleMedium}>
                              Main branches
                            </span>
                          </div>

                          <div className={styles.mainTableWrap}>
                            <table className={styles.mainTable}>
                              <thead>
                                <tr className={styles.mainTableHead}>
                                  <th scope="col">
                                    <span></span>
                                  </th>
                                  <th scope="col">
                                    <span>Proximal</span>
                                  </th>
                                  <th scope="col">
                                    <span>Mid</span>
                                  </th>
                                  <th scope="col">
                                    <span>Distal</span>
                                  </th>
                                </tr>
                              </thead>
                              <tbody>
                                {resolvedSegmentRows.map((row, i) => {
                                  if (i === 0) {
                                    return (
                                      <tr
                                        className={styles.mainTableRow}
                                        key={row.vessel}
                                      >
                                        <td>
                                          <span className={styles.cellHeader}>
                                            {row.vessel}
                                          </span>
                                        </td>
                                        <td>
                                          <span></span>
                                        </td>
                                        <td>
                                          <span className={styles.cellMid}>
                                            {row.mid}
                                          </span>
                                        </td>
                                        <td>
                                          <span></span>
                                        </td>
                                      </tr>
                                    );
                                  } else {
                                    return (
                                      <tr
                                        className={styles.mainTableRow}
                                        key={row.vessel}
                                      >
                                        <td>
                                          <span className={styles.cellHeader}>
                                            {row.vessel}
                                          </span>
                                        </td>
                                        <td>
                                          <span>{row.proximal}</span>
                                        </td>
                                        <td>
                                          <span>{row.mid}</span>
                                        </td>
                                        <td>
                                          <span>{row.distal}</span>
                                        </td>
                                      </tr>
                                    );
                                  }
                                })}
                              </tbody>
                            </table>
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>

                  <div className={styles.rightBottom}>
                    <div className={styles.sideSection}>
                      <div className={styles.header}>
                        <div className={styles.headerLine}></div>
                        <span className={styles.headerTitleMedium}>
                          Side branches
                        </span>
                      </div>
                      <table className={styles.sideTable}>
                        <thead>
                          <tr>
                            {sideData.map((data, index) => (
                              <th
                                className={styles.sideTableHead}
                                key={`${data.name}-head-${index}`}
                                scope="col"
                              >
                                <span>{data.name}</span>
                              </th>
                            ))}
                          </tr>
                        </thead>
                        <tbody>
                          <tr>
                            {sideData.map((data, index) => (
                              <td
                                className={styles.sideTableBody}
                                key={`${data.name}-body-${index}`}
                              >
                                <span>{data.value}</span>
                              </td>
                            ))}
                          </tr>
                        </tbody>
                      </table>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
